import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toolsApi, toolPackagesApi } from '../../lib/apiClient'
import CreateNewToolModal from '../../components/createNewToolModal/CreateNewToolModal'
import { toast } from '../../lib/toast'
import styles from './Tools.module.css'
import { UserRole } from '../../enums/userRole'
import { hasMinRole } from '../../lib/permissions'
import { useAuthStore, useToolsStore, useToolsListViewStore } from '../../store/appStore'
import { useConnectionsStore } from '../../store/connectionsStore'
import ToolsTable from './toolsTable/ToolsTable'
import GeneralFilter from '../../ui/GeneralFilter/GeneralFilter'
import { filterToolsByStatus, filterToolsByMethod, filterToolsByConnection, filterToolsByEnvironment, filterToolsByActiveConnection } from '../../lib/toolsFilters'
import { useTagsStore } from '../../store/tagsStore'
import ToolsFilters from './ToolsFilters/ToolsFilters'
import SecureInput from '../../ui/SecureInput/SecureInput'
import Button from '../../ui/Button/Button'
import ImportToolPackageModal from '../../components/importToolPackageModal/ImportToolPackageModal'
import { Download, Package, Upload } from 'lucide-react'

export default function Tools() {
  const {
    page, searchTerm, statusFilter, methodFilter, connectionFilter,
    environmentFilter, sortBy, selectedTags,
    setPage, setSearchTerm, setStatusFilter, setMethodFilter, setConnectionFilter,
    setEnvironmentFilter, setSortBy, setSelectedTags,
  } = useToolsListViewStore();
  const tags = useTagsStore(state => state.tags);
  const PAGE_SIZE = 10;
  const user = useAuthStore(state => state.user)
  const { tools, toolsLoading, getTools } = useToolsStore(state => state)
  const connectors = useConnectionsStore(state => state.connectors)
  const navigate = useNavigate()
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectedToolIds, setSelectedToolIds] = useState<number[]>([])
  const [savePackageModalOpen, setSavePackageModalOpen] = useState(false)
  const [packageName, setPackageName] = useState('')
  const [packageDescription, setPackageDescription] = useState('')
  const [savingPackage, setSavingPackage] = useState(false)
 
  const loadTools = async () => {
    try {
      await getTools()
    } catch (error: any) {
      toast.error(`Failed to load tools: ${error.message}`)
    }
  }

  const handleExportSelected = async () => {
    if (selectedToolIds.length === 0) {
      toast.error('No tools selected for export')
      return
    }
    try {
      const packageJson = await toolsApi.exportPackage(selectedToolIds)
      const blob = new Blob([JSON.stringify(packageJson, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const safeName = (packageJson.package?.name || 'tools-package').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase()
      a.download = `${safeName}-v${packageJson.package?.version || '1.0.0'}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast.success(`Exported ${selectedToolIds.length} tool(s)`)
    } catch (error: any) {
      toast.error(`Export failed: ${error.message}`)
    }
  }

  const handleSaveAsPackage = () => {
    if (selectedToolIds.length === 0) {
      toast.error('No tools selected')
      return
    }
    setPackageName('')
    setPackageDescription('')
    setSavePackageModalOpen(true)
  }

  const handleConfirmSavePackage = async () => {
    if (!packageName.trim()) {
      toast.error('Please enter a package name')
      return
    }
    setSavingPackage(true)
    try {
      const packageJson = await toolsApi.exportPackage(selectedToolIds)
      await toolPackagesApi.create({
        name: packageName.trim(),
        description: packageDescription.trim() || '',
        version: packageJson.package?.version || '1.0.0',
        connectionTypeId: packageJson.package?.connectionTypeId,
        tools: packageJson.tools,
      })
      toast.success(`Package "${packageName.trim()}" saved with ${selectedToolIds.length} tool(s)`)
      setSelectedToolIds([])
      setSavePackageModalOpen(false)
    } catch (error: any) {
      toast.error(`Failed to save package: ${error.message}`)
    } finally {
      setSavingPackage(false)
    }
  }

  const isViewer = !user || (!hasMinRole(user?.role?.id, UserRole.ToolManager) && user?.role?.id !== UserRole.PromptEditor);

  const filteredAndSortedTools = filterToolsByActiveConnection(
    filterToolsByEnvironment(
      filterToolsByConnection(
        filterToolsByMethod(
          filterToolsByStatus(tools, statusFilter, connectors),
          methodFilter
        ), connectionFilter),
      environmentFilter
    ),
    connectors
  ).filter(tool => {
    const textMatch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tool.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchTerm.toLowerCase())
    const tagMatch = selectedTags.length > 0 ? (tool.tags || []).some(t => selectedTags.includes(t.name)) : true
    return textMatch && tagMatch;
  })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name)
      if (sortBy === 'created_at') return new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime()
      return new Date(b.updated_at!).getTime() - new Date(a.updated_at!).getTime()
    })
    .filter(tool => {
      if (!isViewer) return true;
      return tool.enabled;
    })

  const pageCount = Math.max(1, Math.ceil(filteredAndSortedTools.length / PAGE_SIZE))
  const pagedTools = filteredAndSortedTools.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => {
    setPage(1)
  }, [searchTerm, statusFilter, methodFilter, connectionFilter, environmentFilter, selectedTags])

  useEffect(() => {
    if (page > pageCount) {
      setPage(Math.max(1, pageCount))
    }
  }, [page, pageCount])

  if (toolsLoading) {
    return <div className={styles.loading}>Loading tools...</div>
  }

  const handleConnectionFilterChange = (v: string[] | null) => {
    if (connectionFilter?.includes('all') && v && v.length > 1) {
      v = v.filter(id => id !== 'all')
    }
    if (v && v.includes('all')) {
      v = ['all']
    }
    setConnectionFilter(v)
    setPage(1)
  }

  return (
    <div className={styles.container}>

      <div className={styles.header}>
        <div>
          <h1>MCP Tools</h1>
          <p>Manage your MCP tools</p>
        </div>
        <div className={styles.headerActions}>
          {user && hasMinRole(user?.role?.id, UserRole.ToolManager) && (
            <>
              {selectedToolIds.length > 0 && (
                <>
                  <Button variant="outline" size="md" leftIcon={<Download size={14} />} onClick={handleExportSelected}>
                    Export ({selectedToolIds.length})
                  </Button>
                  {hasMinRole(user?.role?.id, UserRole.SuperAdmin) && (
                    <Button variant="outline" size="md" leftIcon={<Package size={14} />} onClick={handleSaveAsPackage}>
                      Save as Package
                    </Button>
                  )}
                </>
              )}
              <Button variant="outline" size="md" leftIcon={<Upload size={14} />} onClick={() => setImportModalOpen(true)}>
                Import Package
              </Button>

            </>)}
          {user && hasMinRole(user?.role?.id, UserRole.ToolManager) && (
            <Button variant="primary" size="md" onClick={() => setCreateModalOpen(true)}>
              + Create New Tool
            </Button>
          )}
        </div>
      </div>

      <div className={styles.controls}>
        <div className={styles.search}>
          <SecureInput
            fieldType="text"
            placeholder="Search tools..."
            value={searchTerm}
            onChange={(value) => setSearchTerm(value)}
            className={styles.searchInput}
          />
        </div>
        <GeneralFilter
          labelInSelect="Sort by:"
          height={40}
          width={250}
          value={sortBy}
          options={[
            { value: 'name', label: 'Name' },
            { value: 'updated_at', label: 'Updated' },
          ]}
          onChange={v => setSortBy(v as any)}
          className={styles.sort}
        />
        <ToolsFilters
          connectionFilter={connectionFilter}
          onConnectionChange={v => handleConnectionFilterChange(v)}
          statusFilter={statusFilter}
          onStatusChange={v => setStatusFilter(v)}
          methodFilter={methodFilter}
          onMethodChange={v => setMethodFilter(v)}
          environmentFilter={environmentFilter}
          onEnvironmentChange={v => setEnvironmentFilter(v)}
          selectedTags={selectedTags}
          onSelectedTagsChange={setSelectedTags}
          tags={tags}
        />
      </div>

      {filteredAndSortedTools.length === 0 ? (
        <div className={styles.empty}>
          {searchTerm ? (
            <div>
              <h2>No tools found</h2>
              <p>No tools match your search criteria.</p>
            </div>
          ) : (
            <div>
              <h2>No tools created yet</h2>
              <p>Create your first MCP tool or import a tool package to get started.</p>
              {user && hasMinRole(user?.role?.id, UserRole.Admin) && <div className={styles.emptyActions}>
                <Button variant="outline" leftIcon={<Upload size={14} />} onClick={() => setImportModalOpen(true)}>
                  Import Package
                </Button>
                <Button variant="primary" onClick={() => setCreateModalOpen(true)}>
                  Create Your First Tool
                </Button>
              </div>}
            </div>
          )}
        </div>
      ) : (
        <ToolsTable
          setPage={setPage}
          pageCount={pageCount}
          filteredAndSortedTools={pagedTools}
          page={page}
          onTagClick={(tagName: string) => {
            const next = selectedTags.includes(tagName)
              ? selectedTags.filter(t => t !== tagName)
              : [...selectedTags, tagName];
            setSelectedTags(next);
          }}
          selectedToolIds={selectedToolIds}
          onSelectedToolIdsChange={setSelectedToolIds}
        />
      )
      }

      <ImportToolPackageModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImportComplete={loadTools}
      />

      {savePackageModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setSavePackageModalOpen(false)}>
          <div className={styles.savePackageModal} onClick={e => e.stopPropagation()}>
            <h3><Package size={20} /> Save as Package</h3>
            <div>
              <label>Package Name *</label>
              <input
                type="text"
                value={packageName}
                onChange={e => setPackageName(e.target.value)}
                placeholder="e.g. Sales Tools"
                autoFocus
              />
            </div>
            <div>
              <label>Description</label>
              <textarea
                value={packageDescription}
                onChange={e => setPackageDescription(e.target.value)}
                placeholder="Brief description of this package..."
                rows={3}
              />
            </div>
            <div className={styles.modalToolCount}>
              {selectedToolIds.length} tool{selectedToolIds.length !== 1 ? 's' : ''} will be included in this package
            </div>
            <div className={styles.modalActions}>
              <Button variant="outline" size="md" onClick={() => setSavePackageModalOpen(false)} disabled={savingPackage}>
                Cancel
              </Button>
              <Button variant="primary" size="md" onClick={handleConfirmSavePackage} loading={savingPackage}>
                Save Package
              </Button>
            </div>
          </div>
        </div>
      )}

      <CreateNewToolModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreateManually={() => {
          setCreateModalOpen(false)
          navigate('/toolslist/new')
        }}
        onBuildWithAi={() => {
          setCreateModalOpen(false)
          navigate('/toolslist/new?ai=true')
        }}
      />

    </div >
  )
}
